import React, { useEffect, useState } from "react";
import { MotionConfig, motion, useScroll, useSpring } from "framer-motion";
import Lenis from "lenis";

import IntroCurtain from "../../components/admin/landing/IntroCurtain";
import Hero from "../../components/admin/landing/Hero";
import { MarqueeBand, Manifesto } from "../../components/admin/landing/Statement";
import LiveDemo from "../../components/admin/landing/LiveDemo";
import MenuCourses from "../../components/admin/landing/MenuCourses";
import ThemeShowcase from "../../components/admin/landing/ThemeShowcase";
import { Story, Steps } from "../../components/admin/landing/Story";
import PricingBill from "../../components/admin/landing/PricingBill";
import { Faq, FinalCta } from "../../components/admin/landing/Closing";
import { IntroContext, smoothScroll } from "../../components/admin/landing/primitives";
import usePageMeta from "../../util/shared/usePageMeta";

const INTRO_KEY = "ro-intro-seen";

const prefersReducedMotion = () =>
   typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Het gordijn enkel bij het eerste bezoek in een sessie, en nooit bij minder beweging */
const shouldPlayIntro = () => {
   if (prefersReducedMotion()) return false;
   try {
      return !sessionStorage.getItem(INTRO_KEY);
   } catch {
      return false;
   }
};

const HomeAdmin = () => {
   usePageMeta({
      title: "Online bestelsysteem voor horeca | Resto-Online",
      canonical: "https://www.resto-online.be/",
   });
   const [playIntro] = useState(shouldPlayIntro);
   const [introDone, setIntroDone] = useState(!playIntro);

   const { scrollYProgress } = useScroll();
   const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.3 });

   // Lenis: zacht, gewogen scrollen voor de hele pagina
   useEffect(() => {
      if (prefersReducedMotion()) return;
      const lenis = new Lenis({ autoRaf: true, lerp: 0.1, anchors: { offset: -24 } });
      smoothScroll.lenis = lenis;
      // Een link naar /#prijzen (vanaf een andere pagina) landt op de juiste plek
      if (window.location.hash) {
         const target = window.location.hash;
         setTimeout(() => lenis.scrollTo(target, { offset: -24, immediate: true }), 50);
      }
      return () => {
         lenis.destroy();
         smoothScroll.lenis = null;
      };
   }, []);

   // Tijdens het gordijn niet scrollen
   useEffect(() => {
      if (introDone) return;
      smoothScroll.lenis?.stop();
      document.documentElement.style.overflow = "hidden";
      return () => {
         smoothScroll.lenis?.start();
         document.documentElement.style.overflow = "";
      };
   }, [introDone]);

   const finishIntro = () => {
      try {
         sessionStorage.setItem(INTRO_KEY, "1");
      } catch {
         /* privé-venster: dan zie je het gordijn gewoon opnieuw */
      }
      setIntroDone(true);
   };

   return (
      <MotionConfig reducedMotion="user">
         <IntroContext.Provider value={introDone}>
            <div className="lp-root bg-lp-paper text-lp-ink">
               <IntroCurtain show={playIntro} onDone={finishIntro} />

               <motion.div
                  aria-hidden
                  style={{ scaleX: progress }}
                  className="fixed left-0 right-0 top-0 z-[200] h-[2px] origin-left bg-lp-blue"
               />

               <Hero />
               <MarqueeBand />
               <Manifesto />
               <LiveDemo />
               <MenuCourses />
               <ThemeShowcase />
               <Story />
               <Steps />
               <PricingBill />
               <Faq />
               <FinalCta />
            </div>
         </IntroContext.Provider>
      </MotionConfig>
   );
};

export default HomeAdmin;
